import { LitElement, PropertyValues } from 'lit'
import { property } from 'lit/decorators.js'

import { ElementSpacing, Size } from '@/common/types'
import { setFromParentSize } from '@/common/utils'
import {
  type BreakpointName,
  getResponsiveSize,
  parseResponsiveSizeMap,
  subscribeToBreakpoints,
} from '@/common/viewport'

/** All breakpoints; we subscribe to all so effective size updates whenever viewport crosses any breakpoint. */
const ALL_BREAKPOINTS: BreakpointName[] = ['xs', 'sm', 'md', 'lg', 'xl', '2xl']

type Constructor<T = Record<string, unknown>> = {
  new (...args: any[]): T
  prototype: T
}

export interface SizedElementInterface {
  size: Size
  parentSized: string
  spacing: ElementSpacing
  /** Breakpoint→size map; e.g. "md:lg" = at md and narrower use size lg. Keys: xs, sm, md, lg, xl, 2xl. Values: xxs, xs, sm, md, lg. */
  sizeMap: string
  /** The size used for rendering; viewport-adjusted when sizeMap applies, otherwise the requested size. */
  readonly effectiveSize: Size
}

export function SizedMixin<T extends Constructor<LitElement>>(
  constructor: T,
): T & Constructor<SizedElementInterface> {
  class SizedElement extends constructor {
    /** Breakpoint→size map; e.g. "md:lg" applies at md and all narrower breakpoints. Keys: xs, sm, md, lg, xl, 2xl. */
    @property() sizeMap = ''

    @property() size: Size = 'sm'
    @property() parentSized = 'false'
    @property() spacing: ElementSpacing = 'none'

    private _unsubscribeBreakpoints: (() => void) | null = null

    /** When parentSized is true, observes the parent card's size attribute so children re-render when it changes. */
    private _parentSizeObserver: MutationObserver | null = null

    /** User-requested base size; preserved because we overwrite the size attribute with effectiveSize for styling. */
    private _requestedSize: Size | null = null

    /** When true, the next size property change came from our setAttribute (do not store as _requestedSize). */
    private _internalSizeUpdate = false

    /** Resolve sizeMap from property or attribute so it works before Lit has reflected the attribute. */
    private _getSizeMapValue(): string {
      const el = this as unknown as HTMLElement
      return (
        this.sizeMap ||
        el.getAttribute('sizemap') ||
        el.getAttribute('size-map') ||
        ''
      )
    }

    get effectiveSize(): Size {
      const baseSize = this._getBaseSize()
      const map = parseResponsiveSizeMap(this._getSizeMapValue())
      if (!map) return baseSize
      return getResponsiveSize(baseSize, map)
    }

    private _getBaseSize(): Size {
      if (this.parentSized === 'true') {
        const parentSize = (this as unknown as HTMLElement)
          .closest('dap-ds-card')
          ?.getAttribute('size') as Size | null
        return parentSize ?? 'sm'
      }
      const map = parseResponsiveSizeMap(this._getSizeMapValue())
      if (map && this._requestedSize != null) return this._requestedSize
      return this.size
    }

    override connectedCallback(): void {
      super.connectedCallback()
      this._requestedSize =
        (this.getAttribute('size') as Size | null) ?? this.size
      this._updateBreakpointSubscription()
      this._observeParentCardSize()
    }

    override disconnectedCallback(): void {
      this._clearBreakpointSubscription()
      this._clearParentCardSizeObserver()
      super.disconnectedCallback()
    }

    private _observeParentCardSize(): void {
      if (this.parentSized !== 'true') return
      const card = (this as unknown as HTMLElement).closest('dap-ds-card')
      if (!card) return
      this._clearParentCardSizeObserver()
      this._parentSizeObserver = new MutationObserver(() => {
        if (this.isConnected) this.requestUpdate()
      })
      this._parentSizeObserver.observe(card, {
        attributes: true,
        attributeFilter: ['size'],
      })
    }

    private _clearParentCardSizeObserver(): void {
      this._parentSizeObserver?.disconnect()
      this._parentSizeObserver = null
    }

    private _updateBreakpointSubscription(): void {
      const map = parseResponsiveSizeMap(this._getSizeMapValue())
      if (!map) {
        this._clearBreakpointSubscription()
        return
      }
      this._clearBreakpointSubscription()
      // Subscribe to all breakpoints so effective size updates whenever viewport
      // crosses any boundary; otherwise we only revert to base size when crossing
      // a breakpoint that was in the map (e.g. xs:md only at xs → must update when leaving xs).
      this._unsubscribeBreakpoints = subscribeToBreakpoints(
        ALL_BREAKPOINTS,
        () => {
          if (this.isConnected) this.requestUpdate()
        },
      )
    }

    private _clearBreakpointSubscription(): void {
      this._unsubscribeBreakpoints?.()
      this._unsubscribeBreakpoints = null
    }

    protected override update(changes: PropertyValues): void {
      if (changes.has('sizeMap')) {
        this._updateBreakpointSubscription()
      }
      if (changes.has('parentSized')) {
        if (this.parentSized === 'true') {
          this._observeParentCardSize()
        } else {
          this._clearParentCardSizeObserver()
        }
      }
      const map = parseResponsiveSizeMap(this._getSizeMapValue())
      if (map && this._requestedSize == null) this._requestedSize = this.size
      // Subscribe on first update when sizeMap came from attribute (no change event)
      if (map && !this._unsubscribeBreakpoints) {
        this._updateBreakpointSubscription()
      }
      if (changes.has('size') && !this._internalSizeUpdate) {
        this._requestedSize = this.size
      }
      if (this._internalSizeUpdate) this._internalSizeUpdate = false
      if (this.parentSized === 'true') {
        setFromParentSize(this as unknown as HTMLElement)
      }
      this._internalSizeUpdate = true
      this.setAttribute('size', this.effectiveSize)
      super.update(changes)
    }
  }
  return SizedElement as Constructor<SizedElementInterface> & T
}
